import {noop} from 'lodash';
import React, {PropsWithChildren, useCallback, useContext, useMemo, useState} from 'react';

import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';

/*
 * Consts.
 */

export enum SafetyNetActionsEnum {
  ARCHIVE = 'ARCHIVE',
  MOVE = 'MOVE',
  SEND_AND_ARCHIVE = 'SEND_AND_ARCHIVE',
  SEND_AND_SNOOZE = 'SEND_AND_SNOOZE',
  SEND = 'SEND',
}

export const requiredTaggingActions = [
  SafetyNetActionsEnum.ARCHIVE,
  SafetyNetActionsEnum.MOVE,
  SafetyNetActionsEnum.SEND_AND_ARCHIVE,
];

/*
 * Types.
 */

export interface SafetyNetAction {
  type: SafetyNetActionsEnum;
  onActionValidated: Handler;
  conversations: ReadonlyArray<ConversationBaseModel>;
}

/*
 * Context.
 */

export interface SafetyNetsContextState {
  shouldShowBlockingModal: boolean;
  maybeAction: SafetyNetAction | undefined;

  onHideBlockingModal: Handler;
  onShowBlockingModal: HandlerOf<SafetyNetAction>;
}

const defaultSafetyNetsContextState: SafetyNetsContextState = {
  shouldShowBlockingModal: false,
  maybeAction: undefined,

  onHideBlockingModal: noop,
  onShowBlockingModal: noop,
};

const SafetyNetsContext = React.createContext<SafetyNetsContextState>(defaultSafetyNetsContextState);

/*
 * Component.
 */

export const SafetyNetsProvider: React.FC<PropsWithChildren<unknown>> = ({children}) => {
  const [action, setAction] = useState<SafetyNetAction>();

  const onHideBlockingModal = useCallback(() => {
    setAction(undefined);
  }, []);

  const onShowBlockingModal = useCallback((actionToSet: SafetyNetAction) => {
    setAction(actionToSet);
  }, []);

  const context = useMemo(
    () => ({
      shouldShowBlockingModal: action !== undefined,
      maybeAction: action,

      onHideBlockingModal,
      onShowBlockingModal,
    }),
    [action, onHideBlockingModal, onShowBlockingModal],
  );

  return <SafetyNetsContext.Provider value={context}>{children}</SafetyNetsContext.Provider>;
};

/*
 * Helpers.
 */

export function useSafetyNets() {
  return useContext(SafetyNetsContext);
}
